import React, { useEffect, useMemo, useState } from 'react';
import { Users, ClipboardList, Plus, X } from 'lucide-react';
import { CommanServices } from '@/services/commanServices';
import { cleanRoles, ROLE_OPTIONS } from './ScenarioRoleField';
import StaffingPlanEditor from './StaffingPlanEditor';
import Combobox from './ui/combobox';

// Scenario role catalog plus a read-only candidate union derived from saved
// cards. Per-card headcount and candidate management stay inside each inject.
export default function AssignedRolesWidget({ incident, scenarioId }) {
  const [items, setItems] = useState([]);
  const [staffingDefaults, setStaffingDefaults] = useState({ roles: {} });
  const [addingRole, setAddingRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [roleError, setRoleError] = useState('');
  const [savingRole, setSavingRole] = useState(false);

  const load = async () => {
    if (!incident?.id) return;
    try {
      const res = await CommanServices.getincidentTimeline(incident.id);
      if (res?.status) setItems((await res.data) || []);
    } catch (e) {
      console.error('AssignedRolesWidget load error:', e);
    }
  };

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener('timelineRefresh', h);
    window.addEventListener('incidentRealtimeSync', h);
    return () => {
      window.removeEventListener('timelineRefresh', h);
      window.removeEventListener('incidentRealtimeSync', h);
    };
  }, [incident?.id]);

  // Drills inherit the roles of the scenario they were launched from.
  const sourceScenarioId = scenarioId || incident?.source_scenario_id || null;
  useEffect(() => {
    if (!sourceScenarioId) return;
    CommanServices.getScenario(sourceScenarioId).then((res) => {
      if (res?.status) setStaffingDefaults(res.data?.staffing_defaults || { roles: {} });
    }).catch(() => {});
  }, [sourceScenarioId]);

  useEffect(() => {
    if (!scenarioId) return undefined;
    const syncStaffingDefaults = (event) => setStaffingDefaults(event?.detail || { roles: {} });
    window.addEventListener('scenarioStaffingDefaultsUpdated', syncStaffingDefaults);
    return () => window.removeEventListener('scenarioStaffingDefaultsUpdated', syncStaffingDefaults);
  }, [scenarioId]);

  const { roleTotals, totalPeople, totalDefaultCandidates, defaultStaffingRoles, readOnlyStaffingDefaults, instructionsByRole } = useMemo(() => {
    const totals = {};
    let people = 0;
    const candidatesByRole = {};
    const candidateKeysByRole = {};
    const instructionEntriesByRole = {};
    const cardRoleNames = new Set();
    Object.entries(staffingDefaults?.roles || {}).forEach(([role, config]) => {
      const name = String(role || '').trim();
      if (name) totals[name] = scenarioId ? 1 : Math.max(totals[name] || 0, Number(config?.required_count) || 1);
      if (name) {
        candidatesByRole[name] = [];
        candidateKeysByRole[name] = new Set();
        instructionEntriesByRole[name] = new Map();
      }
    });
    (items || []).forEach((it) => {
      if (!it || it.is_active === false || it.group === 'report') return;
      const meta = it?.meta && typeof it.meta === 'object' ? it.meta : {};
      const rawRoles = Array.isArray(meta.scenarioRoles) ? meta.scenarioRoles : [];
      if (!rawRoles.length) return;
      rawRoles.forEach((rawRole) => {
        const r = cleanRoles([rawRole])[0];
        if (!r) return;
        cardRoleNames.add(r.role);
        if (!scenarioId) totals[r.role] = Math.max(totals[r.role] || 0, r.count);
        if (!Object.prototype.hasOwnProperty.call(candidatesByRole, r.role)) {
          candidatesByRole[r.role] = [];
          candidateKeysByRole[r.role] = new Set();
          instructionEntriesByRole[r.role] = new Map();
        }

        const hasRoleInstructions = Object.prototype.hasOwnProperty.call(rawRole || {}, 'instructions');
        const instruction = String(
          hasRoleInstructions ? (rawRole?.instructions ?? '') : (meta.assignmentNote ?? ''),
        ).trim();
        if (instruction) {
          const groupLabel = ({
            acknowledgment: 'Verification',
            notification: 'Notification',
            response_action: 'Response action',
            pre_incident_action: 'Pre-incident action',
            status_update: 'Status update',
            resource: 'Resource',
          })[it.group] || 'Injected card';
          const cardTitle = String(it.title || '').trim();
          const sourceLabel = cardTitle ? `${groupLabel} · ${cardTitle}` : groupLabel;
          const instructionKey = instruction.toLocaleLowerCase();
          const existing = instructionEntriesByRole[r.role].get(instructionKey);
          const source = { id: String(it.id || sourceLabel), label: sourceLabel };
          if (existing) {
            if (!existing.sources.some((entry) => entry.id === source.id)) existing.sources.push(source);
          } else {
            instructionEntriesByRole[r.role].set(instructionKey, { text: instruction, sources: [source] });
          }
        }

        [
          ...(Array.isArray(r.staffing?.preferred) ? r.staffing.preferred : []),
          ...(Array.isArray(r.staffing?.fallbacks) ? r.staffing.fallbacks : []),
        ].forEach((candidate) => {
          const key = `${candidate?.type || ''}:${candidate?.id || ''}`;
          if (!candidate?.type || !candidate?.id || candidateKeysByRole[r.role].has(key)) return;
          candidateKeysByRole[r.role].add(key);
          candidatesByRole[r.role].push(candidate);
        });
      });
    });
    const roleTotals = Object.entries(totals).sort((a, b) => b[1] - a[1]);
    people = roleTotals.reduce((sum, [, count]) => sum + count, 0);
    const catalogRoles = Object.keys(staffingDefaults?.roles || {});
    const defaultStaffingRoles = [
      ...catalogRoles.filter((role) => cardRoleNames.has(role)),
      ...[...cardRoleNames]
        .filter((role) => !Object.prototype.hasOwnProperty.call(staffingDefaults?.roles || {}, role))
        .sort((a, b) => a.localeCompare(b)),
    ];
    const readOnlyRoles = Object.fromEntries(
      defaultStaffingRoles.map((role) => [role, {
        ...(staffingDefaults?.roles?.[role] || {}),
        required_count: 1,
        preferred: candidatesByRole[role] || [],
        fallbacks: [],
      }]),
    );
    const candidateCount = Object.values(candidatesByRole).reduce((sum, rows) => sum + rows.length, 0);
    const roleInstructions = Object.fromEntries(
      Object.entries(instructionEntriesByRole).map(([role, entries]) => [role, [...entries.values()]]),
    );
    return {
      roleTotals,
      totalPeople: people,
      totalDefaultCandidates: candidateCount,
      defaultStaffingRoles,
      readOnlyStaffingDefaults: { roles: readOnlyRoles },
      instructionsByRole: roleInstructions,
    };
  }, [items, staffingDefaults, scenarioId]);

  const recommendedRoleOptions = useMemo(() => {
    const existingKeys = new Set(Object.keys(staffingDefaults?.roles || {}).map((role) => role.toLowerCase()));
    return ROLE_OPTIONS
      .filter((role) => !existingKeys.has(role.toLowerCase()))
      .map((role) => ({ value: role, label: role }));
  }, [staffingDefaults]);

  const addRequiredRole = async (event) => {
    event?.preventDefault?.();
    const role = newRoleName.trim();
    if (!role) { setRoleError('Enter a role name.'); return; }
    if (role.includes('.') || role.startsWith('$')) { setRoleError('Role names cannot contain “.” or start with “$”.'); return; }
    const duplicate = Object.keys(staffingDefaults?.roles || {}).find((name) => name.toLowerCase() === role.toLowerCase());
    if (duplicate) { setRoleError('This required role already exists.'); return; }
    setSavingRole(true);
    setRoleError('');
    try {
      const next = {
        roles: {
          ...(staffingDefaults?.roles || {}),
          [role]: {
            mode: 'fill_slots',
            required_count: 1,
            preferred: [],
            fallbacks: [],
          },
        },
      };
      const res = await CommanServices.saveScenarioStaffingDefaults(scenarioId, next);
      if (res?.status === false) throw new Error(res.message || 'Unable to add role');
      setStaffingDefaults(res?.data || next);
      window.dispatchEvent(new CustomEvent('scenarioStaffingDefaultsUpdated', { detail: res?.data || next }));
      setNewRoleName('');
      setAddingRole(false);
    } catch (error) {
      setRoleError(error?.message || 'Unable to add role.');
    } finally {
      setSavingRole(false);
    }
  };

  return (
    <div className="bg-white" data-testid="assigned-roles-widget">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-violet-100 bg-violet-50 px-4 py-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Users className="h-4 w-4 text-violet-600" />
          <h3 className="text-sm font-bold text-violet-900">Required Roles</h3>
          <span className="rounded-full border border-violet-200 bg-white px-2 py-0.5 text-[10px] font-bold text-violet-700" data-testid="assigned-roles-total">
            {scenarioId
              ? `${roleTotals.length} ${roleTotals.length === 1 ? 'role' : 'roles'} · ${totalDefaultCandidates} ${totalDefaultCandidates === 1 ? 'candidate' : 'candidates'}`
              : `${totalPeople} role slots · ${roleTotals.length} roles`}
          </span>
        </div>
        {scenarioId && !addingRole && (
          <button type="button" onClick={() => { setAddingRole(true); setNewRoleName(''); setRoleError(''); }}
            className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2.5 py-1.5 text-[11px] font-bold text-white hover:bg-violet-500"
            data-testid="add-required-role-btn">
            <Plus className="h-3.5 w-3.5" /> Add role
          </button>
        )}
      </div>

      <div className="space-y-3 p-4">
        {scenarioId && addingRole && (
          <form onSubmit={addRequiredRole} className="rounded-lg border border-violet-200 bg-violet-50/70 p-3" data-testid="add-required-role-form">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-xs font-bold text-violet-900">Add required role</p>
              <button type="button" onClick={() => { setAddingRole(false); setNewRoleName(''); setRoleError(''); }} className="rounded p-1 text-violet-500 hover:bg-violet-100" aria-label="Close add role form">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-violet-700">Role</p>
                <Combobox
                  value={newRoleName}
                  onValueChange={(role) => { setNewRoleName(role); setRoleError(''); }}
                  options={recommendedRoleOptions}
                  placeholder="Search or create a role…"
                  searchPlaceholder="Search recommended roles or type a new one…"
                  optionsLabel="Recommended disaster roles"
                  createLabel="Use custom role"
                  emptyLabel="No matching recommended role"
                  className="mt-1 h-9 border-violet-200 bg-white text-xs text-slate-900 hover:bg-white"
                  testId="required-role-search"
                />
                <p className="mt-1 text-[10px] text-violet-600">Choose a recommendation or type a custom role, then add it.</p>
              </div>
              <button type="submit" disabled={savingRole} className="rounded-md bg-violet-600 hover:bg-violet-500 disabled:opacity-50 px-3 py-2 text-xs font-bold text-white">
                {savingRole ? 'Adding…' : 'Add role'}
              </button>
            </div>
            {roleError && <p className="mt-2 text-[11px] font-medium text-red-600">{roleError}</p>}
          </form>
        )}
        {roleTotals.length === 0 ? (
          <div className="rounded-lg border border-dashed border-violet-200 bg-violet-50/40 p-4 text-center">
            <ClipboardList className="h-5 w-5 text-violet-300 mx-auto" />
            <p className="mt-2 text-xs font-semibold text-violet-900">No required roles yet</p>
            <p className="mt-1 text-[11px] text-gray-500">Add a role here, then choose its candidates inside an injected-card modal.</p>
          </div>
        ) : (
          <StaffingPlanEditor
            roles={defaultStaffingRoles.map((role) => ({ role }))}
            initialPlan={readOnlyStaffingDefaults}
            instructionsByRole={instructionsByRole}
          />
        )}
      </div>
    </div>
  );
}
